// Advanced Script Loader for TBT Optimization
class ScriptLoader {
  constructor() {
    this.loadedScripts = new Set();
    this.loadingPromises = new Map();
    this.criticalScriptsLoaded = false;
  }

  // Load script with priority and caching
  loadScript(src, options = {}) {
    const { priority = 'low', condition = true, dependencies = [] } = options;
    
    if (!condition || this.loadedScripts.has(src)) {
      return Promise.resolve();
    }

    if (this.loadingPromises.has(src)) {
      return this.loadingPromises.get(src);
    }

    const promise = this.createScriptPromise(src, priority, dependencies);
    this.loadingPromises.set(src, promise);
    
    return promise;
  }

  createScriptPromise(src, priority, dependencies) {
    return new Promise((resolve, reject) => {
      // Wait for dependencies first
      Promise.all(dependencies.map(dep => this.loadScript(dep)))
        .then(() => {
          const script = document.createElement('script');
          script.src = src;
          script.defer = true;
          
          script.onload = () => {
            this.loadedScripts.add(src);
            this.loadingPromises.delete(src);
            resolve();
          };
          
          script.onerror = () => {
            this.loadingPromises.delete(src);
            reject(new Error(`Failed to load script: ${src}`));
          };

          // Use scheduler API if available for better performance
          if (priority === 'high' || !('scheduler' in window)) {
            document.head.appendChild(script);
          } else {
            this.scheduleScriptLoad(script, priority);
          }
        })
        .catch(reject);
    });
  }

  scheduleScriptLoad(script, priority) {
    const taskPriority = priority === 'high' ? 'user-blocking' : 
                        priority === 'medium' ? 'user-visible' : 'background';
    
    if ('scheduler' in window && 'postTask' in scheduler) {
      scheduler.postTask(() => {
        document.head.appendChild(script);
      }, { priority: taskPriority });
    } else if ('requestIdleCallback' in window && priority === 'low') {
      requestIdleCallback(() => {
        document.head.appendChild(script);
      });
    } else {
      setTimeout(() => {
        document.head.appendChild(script);
      }, priority === 'high' ? 0 : 100);
    }
  }

  // Load scripts based on viewport intersection
  loadOnIntersection(selector, scripts) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          scripts.forEach(script => this.loadScript(script.src, script.options));
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '50px' });

    document.querySelectorAll(selector).forEach(el => observer.observe(el));
  }

  // Load scripts on user interaction
  loadOnInteraction(scripts, events = ['mousedown', 'touchstart', 'keydown']) {
    const loadScripts = () => {
      scripts.forEach(script => this.loadScript(script.src, script.options));
      events.forEach(event => document.removeEventListener(event, loadScripts));
    };

    events.forEach(event => {
      document.addEventListener(event, loadScripts, { once: true, passive: true });
    });
  }
}

// Initialize global script loader
window.scriptLoader = new ScriptLoader();

// Optimize script loading based on page type and user behavior
document.addEventListener('DOMContentLoaded', () => {
  const loader = window.scriptLoader;

  // Note: This script loader is designed to work with proper Shopify asset URLs
  // For now, we'll disable automatic loading to prevent 404 errors
  // Individual scripts should be loaded by their respective sections
  
  console.log('Script loader initialized - ready for manual script loading');
});

// Export for use in other scripts
window.loadScript = (src, options) => window.scriptLoader.loadScript(src, options);